/* Единая мобильная шапка и сайдбар (MobileShellHeader). Компонент существует
   только под мобильным порогом (см. useIsMobile.ts) — на десктопе он не
   рендерится вовсе, поэтому здесь нет ни одного @media: то, что видно здесь,
   видно всегда, когда оно вообще есть в DOM. */

/* Отступ сверху — под чёлку и часы: в приложении с домашнего экрана страница
   начинается от самого верха экрана (viewport-fit=cover + status-bar-style
   black-translucent, см. index.html), и без него лого-полоса лежала бы прямо
   под системными часами. В обычной вкладке браузера env(...) равен нулю, так
   что здесь ничего не меняется. */
.header {
  display: flex;
  flex-direction: column;
  padding-top: env(safe-area-inset-top, 0px);
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}

/* A1: лого-полоса — тонкая строка во всю ширину экрана. Раскладку (слово
   слева, иконки справа, одна строка без переносов) держит сам Logo в режиме
   layout="bar" — здесь только воздух по краям и волосок, отделяющий полосу
   от заголовка под ней. */
.logoBar {
  display: flex;
  align-items: center;
  padding: var(--sp-2) var(--sp-4);
  border-bottom: 1px solid var(--border);
}

/* A2: вторая строка — заголовок слева, кнопка меню справа. min-width: 0 на
   заголовке обязателен: без него флекс-элемент не сжимается ниже своего
   содержимого, и длинное название поездки распирало бы строку, выталкивая
   кнопку меню за край экрана вместо того, чтобы обрезаться многоточием. */
.titleRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  padding: var(--sp-2) var(--sp-4);
  min-height: 48px;
}

.title {
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--text);
  font-size: var(--fs-lg);
  font-weight: 600;
}

/* Кнопка меню: бургер, силуэт человека, имя — в этом порядке слева направо
   (см. компонент). Заменяет разом и прежний бургер, и кнопку аккаунта, и
   доросла у неё содержимого до размера настоящей кнопки с рамкой, а не
   квадратной иконки. */
.menuBtn {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  flex: none;
  max-width: 60%;
  min-height: 44px;
  padding: var(--sp-2) var(--sp-3);
  background: transparent;
  border: var(--bw) solid var(--border);
  border-radius: var(--radius);
  color: var(--text);
  cursor: pointer;
}

.menuBtn:hover {
  background: var(--surface-2);
}

.menuBtn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

.menuBtn svg {
  flex: none;
}

.menuBtnLabel {
  min-width: 0;
  max-width: 12ch;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: var(--fs-sm);
  font-weight: 600;
}

/* Однострочный вариант шапки — гость в планировщике, у которого нет
   заголовка (см. проп title у компонента). Одна строка вместо лого-полосы и
   строки заголовка: знак прижат к левому краю, кнопка меню — к правому, как
   и в двухстрочном варианте, где так же стоят заголовок и та же кнопка. Знак
   тут занимает место заголовка, и разъезжаться этой паре по краям — то же
   самое правило, а не исключение ради одного состояния.
   flex-wrap: nowrap запрещает перенос на любой ширине. position: relative —
   точка отсчёта для .probe внутри: он должен мериться от той же строки,
   которую сам не должен раздвигать. */
.singleLine {
  position: relative;
  /* .probe внутри специально может оказаться шире строки — это и есть
     сигнал «иконки не помещаются». overflow: hidden не даёт этому невидимому
     двойнику раздвинуть скролл страницы вбок: сам он не виден и не кликается
     (visibility: hidden, pointer-events: none в .probe), а видимое
     содержимое строки к моменту отрисовки уже подобрано так, что помещается
     и обрезаться этим правилом не может. */
  overflow: hidden;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  padding: var(--sp-2) var(--sp-4);
  min-height: 48px;
}

/* Невидимый двойник знака для замера (см. useIconsFit в компоненте).
   position: absolute убирает его из потока — он ничего не раздвигает и не
   участвует в раскладке видимых элементов, — а без явной width сам
   сжимается по содержимому, поэтому его собственная ширина после замера и
   есть «сколько нужно места знаку со всеми иконками». visibility: hidden,
   а не display: none, — второе убрало бы его из расчёта размеров вовсе. */
.probe {
  position: absolute;
  top: 0;
  left: 0;
  visibility: hidden;
  pointer-events: none;
  white-space: nowrap;
}

/* ── Сайдбар (B3) ─────────────────────────────────────────────────────────
   Тот же приём затемнения и портального слоя, что у бургера (AppMenu),
   только выезжает не узкой панелью, а во весь экран — компонент существует
   лишь на телефонной ширине, отдельного брейкпоинта под это не нужно. */
.overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  background: var(--overlay);
}

.sheet {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100dvh;
  /* B4: поверхность активной темы + размытие и кромка света. В обычных темах
     --glass-blur/--glass-rim пустые, а --surface непрозрачен (см. комментарий
     в styles/themes.css) — тогда это просто сплошная подложка без эффекта. В
     Liquid Glass токены заполнены и подложка становится настоящим стеклом.
     Ровно один слой токена: раньше здесь лежали два одинаковых слоя друг на
     друге, чтобы alpha-over-alpha добрала непрозрачности сверх той, что задаёт
     тема (0.44 → ~0.69). Плотность выходила не темы, и на светлом стекле
     панель читалась молочной заплатой поверх темы, а не её поверхностью. */
  background: var(--surface);
  -webkit-backdrop-filter: var(--glass-blur);
  backdrop-filter: var(--glass-blur);
  box-shadow: var(--glass-rim), var(--shadow-modal);
}

.sheet:focus {
  outline: none;
}

.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-4);
  flex: none;
  padding: var(--sp-5);
  border-bottom: 1px solid var(--border);
}

.headTitle {
  font-size: var(--fs-lg);
  font-weight: 600;
}

.closeBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex: none;
  background: transparent;
  border: var(--bw) solid transparent;
  border-radius: var(--radius);
  color: var(--text-2);
  font-size: var(--fs-lg);
  cursor: pointer;
}

.closeBtn:hover {
  background: var(--surface-2);
  color: var(--text);
}

.closeBtn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

.body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--sp-5);
}

.section + .section {
  margin-top: var(--sp-6);
  padding-top: var(--sp-6);
  border-top: 1px solid var(--border);
}

.legend {
  margin: 0 0 var(--sp-3);
  color: var(--text-3);
  font-size: var(--fs-xs);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.rows {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

/* Контролы раздела «Поездка» под палец, а не под курсор доразмеряет сам
   Toolbar.module.css (класс .menuRow, который Toolbar передаёт каждой строке)
   — здесь этого правила нет намеренно: дублировать его означало бы держать
   одну и ту же высоту в двух файлах и рано или поздно развести их. Строки
   раздела «Аккаунт» тем же способом доразмеряет AccountMenu.module.css у
   себя (там это @media (max-width: 720px), который здесь всегда активен). */

/* B5: футер прибит к низу панели. margin-top: auto на самом футере (а не
   flex-grow на .body) — так .body остаётся тем, что растягивается и
   скроллится, а футер держит свою высоту по контенту и просто прижимается
   вниз тем, что осталось. env(safe-area-inset-bottom) — запас под системную
   полосу жестов на телефонах с вырезом снизу. */
.footer {
  flex: none;
  margin-top: auto;
  padding: var(--sp-4) var(--sp-5) calc(var(--sp-4) + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--border);
}

.signOutBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  width: 100%;
  min-height: 48px;
  padding: var(--sp-3);
  background: none;
  border: var(--bw) solid var(--border);
  border-radius: var(--radius);
  color: var(--text);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.signOutBtn:hover {
  background: var(--surface-2);
}

.signOutBtn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}
